import { ApplyCupomApi } from "@/services/backend/cart";
import request_anonymous from '@/plugins/axios_anonymous';
import { unset } from "lodash";
import { defineStore } from "pinia";
import { ref, computed, watch, reactive, toRaw } from "vue";
import { Notification } from "@/plugins/notifications";
import { useSystemStore } from "./SystemStore";
import { useAtendimentosStore } from "./AtendimentosStore";

export const useCartStore = defineStore('cart', () => {
  const systemStore = useSystemStore()
  const atendimentosStore = useAtendimentosStore()

  const showCart = ref(false)
  const cartItems = reactive({
    ecommerce: {},
    affiliate: {},
  })
  const owner = ref(window.location.host)
  const affiliateCode = ref(null)
  const alertAddCartItems = ref(false)
  const Cupom = ref('')
  const CupomProdutos = ref([])
  const Desconto = ref(0)
  const DescontoCarrinho = ref(0)
  const Installments = ref([])
  const InterestRateInPercent = ref(0)
  const couponApplied = ref(false)

  const cartItemsCumputed = computed(() => GetCartItems())
  const TotalItensInCart = computed(() => cartItemsCumputed.value.reduce((soma, item) => soma + item.quantidade, 0))
  const Subtotal = computed(() => cartItemsCumputed.value.reduce((soma, item) => soma + item.quantidade * item.valor, 0))
  const Total = computed(() => Subtotal.value - Desconto.value)
  const ShoppingCart = computed(() => {
    return {
      cartItems: cartItemsCumputed.value,
      TotalItensInCart: TotalItensInCart.value,
      Subtotal: Subtotal.value,
      Desconto: Desconto.value,
      Total: Total.value,
      alertAddCartItems: alertAddCartItems.value
    }
  })


  /**
   * Função para retornar os itens do carrinho de compras do ecommerce ou da loja do afiliado.
   * @returns {Array}
   */
  function GetCartItems() {
    if (affiliateCode.value) {
      if (!cartItems.affiliate[affiliateCode.value]) {
        cartItems.affiliate[affiliateCode.value] = []
      }
      return cartItems.affiliate[affiliateCode.value]
    } else {
      if (!cartItems.ecommerce[owner.value]) {
        cartItems.ecommerce[owner.value] = []
      }
      return cartItems.ecommerce[owner.value]
    }
  }

  function SetAffiliateCode(code) {
    affiliateCode.value = code
  }

  /**
   * O mesmo produto pode existir em mais de uma linha do carrinho, uma por modalidade
   * (ver items 1 do plano de melhorias do fluxo de compra) — por isso a identidade de um
   * item do carrinho é a combinação (uuid do produto/promoção, modalidade_uuid), nunca só
   * o uuid. `null`/`undefined` representa a linha "Padrão" (sem modalidade específica).
   */
  function getModalidadeKey(modalidade_uuid) {
    return modalidade_uuid || 'padrao'
  }

  function findCartItem(uuid, modalidade_uuid = null) {
    return cartItemsCumputed.value.find(
      item => item.uuid === uuid && getModalidadeKey(item.modalidade_uuid) === getModalidadeKey(modalidade_uuid)
    )
  }

  // Identificador aleatório só para reconciliação de lista no Vue (:key) — não precisa de força
  // criptográfica, por isso não depende só de crypto.randomUUID (exige contexto seguro/HTTPS).
  function generateLineId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID()
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`
  }

  function AddProductToCart(product, isPromotion = false) {

    const exist = findCartItem(product.uuid, null)
    if (exist) {
      if (!exist.quantidade) {
        exist.quantidade = 1
      }
      exist.quantidade++
    } else {
      // Clona o produto antes de guardar no carrinho: `product` é a mesma referência usada
      // pela store de catálogo/listagem/promoções. Sem clonar, as mutações de modalidade feitas
      // no carrinho (ChangeItemModality) corrompem o preço exibido na listagem, já que ambos
      // apontariam para o mesmo objeto.
      // toRaw é necessário: `product` normalmente é (ou contém) um Proxy reativo do Vue/Pinia
      // (vem da store de catálogo/promoções), e structuredClone não consegue clonar Proxies.
      const item = structuredClone(toRaw(product))
      item.default_price = item.valor
      item.modalidade_uuid = null
      item.quantidade = 1
      item.isPromotion = isPromotion
      // Identificador estável da linha, independente de campos mutáveis como modalidade_uuid —
      // usado como :key no v-for do carrinho para o componente da linha não desmontar/remontar
      // (perdendo estado local, como o aviso de CNH) quando a modalidade dela é alterada no lugar.
      item._cartLineId = generateLineId()
      cartItemsCumputed.value.push(item)
      if (Cupom.value !== '') {
        ApplyCupom(Cupom.value)
      }
    }

    alertAddCartItems.value = systemStore.IsMobile ? false : true
  }

  /**
   * Altera a modalidade de `quantidade_para_mudar` unidades da linha (produto, modalidade_origem)
   * para `modalidade_destino`:
   * - Se `quantidade_para_mudar` cobre a linha inteira, a própria linha de origem muda de
   *   modalidade no lugar; se já existir uma linha para a modalidade de destino, a linha de
   *   ORIGEM absorve a quantidade dela e a linha de destino é removida (nunca deixamos duas
   *   linhas com a mesma combinação produto+modalidade). A linha de origem — que é a instância
   *   que disparou a troca na UI — nunca é removida, só a de destino, para não desmontar o
   *   componente que o usuário está usando no meio da ação (ex.: aviso de CNH em seguida).
   * - Se for só uma parte, a linha de origem tem sua quantidade reduzida (permanece com pelo
   *   menos 1 unidade) e uma nova linha é criada (ou incrementada, se já existir) para a
   *   modalidade de destino.
   * Usada tanto para o caso trivial (1 unidade, troca direta) quanto para o caso com escolha do
   * usuário (N unidades, "alterar todas" vs. "alterar algumas") — a UI decide o valor de
   * `quantidade_para_mudar` antes de chamar esta função.
   */
  async function ChangeItemModality(uuid, modalidade_origem, modalidade_destino, quantidade_para_mudar) {
    const origem = findCartItem(uuid, modalidade_origem)
    if (!origem) return

    const qtd = Math.min(quantidade_para_mudar ?? origem.quantidade, origem.quantidade)
    if (qtd <= 0 || getModalidadeKey(modalidade_origem) === getModalidadeKey(modalidade_destino)) return

    const destino = findCartItem(uuid, modalidade_destino)
    const valorDestino = modalidade_destino
      ? Number(origem.modalidade_valores?.find(m => m.modalidade === modalidade_destino)?.valor ?? origem.default_price ?? origem.valor)
      : Number(origem.default_price ?? origem.valor)

    if (qtd >= origem.quantidade) {
      // Muda a linha inteira: a origem sempre sobrevive, absorvendo o destino se ele já existir.
      if (destino) {
        origem.quantidade += destino.quantidade
        RemoveProductFromCart(uuid, modalidade_destino)
      }
      origem.valor = valorDestino
      origem.modalidade_uuid = modalidade_destino || null
    } else {
      // Muda só uma parte: reduz a origem e move o restante para o destino.
      origem.quantidade -= qtd
      if (destino) {
        destino.quantidade += qtd
      } else {
        cartItemsCumputed.value.push({
          ...origem,
          valor: valorDestino,
          modalidade_uuid: modalidade_destino || null,
          quantidade: qtd,
          valor_com_desconto: undefined,
          _cartLineId: generateLineId(),
        })
      }
    }

    if (couponApplied.value) {
      await ApplyCupom(Cupom.value)
    }
  }

  function RemoveProductFromCart(product_uuid, modalidade_uuid = null) {
    const items = GetCartItems()
    const index = items.findIndex(
      item => item.uuid === product_uuid && getModalidadeKey(item.modalidade_uuid) === getModalidadeKey(modalidade_uuid)
    )
    if (index === -1) return
    if (couponApplied.value) {
      unset(items[index], 'valor_com_desconto')
    }
    items.splice(index, 1)
  }

  function CleanPurchaseCart() {
    RemoveCupom()
    const items = GetCartItems()
    items.splice(0, items.length)
    Desconto.value = 0
  }

  function IncrementProductInCart(uuid, modalidade_uuid = null) {
    const item = findCartItem(uuid, modalidade_uuid)
    if (item) {
      item.quantidade++
    }
  }

  function DecrementProductInCart(uuid, modalidade_uuid = null) {
    const items = GetCartItems()
    const exist = findCartItem(uuid, modalidade_uuid)
    if (exist) {
      if (exist.quantidade > 1) {
        exist.quantidade--
      } else {
        const index = items.findIndex(
          item => item.uuid === uuid && getModalidadeKey(item.modalidade_uuid) === getModalidadeKey(modalidade_uuid)
        )
        if (index !== -1) {
          items.splice(index, 1)
        }
      }
    }
  }

  async function ApplyCupom(cupom) {
    if (cupom.length <= 0 || cupom === null) {
      RemoveCupom()
      return
    }
    // Pareado por posição com response.data.produtos/promocoes na resposta — não por uuid,
    // já que o mesmo produto pode aparecer em mais de uma linha do carrinho (modalidades
    // diferentes) com o mesmo uuid.
    const produtosItems = cartItemsCumputed.value.filter(item => !item.isPromotion)
    const promocoesItems = cartItemsCumputed.value.filter(item => item.isPromotion)

    const produtos = produtosItems.map(item => ({
      uuid: item.uuid,
      quantidade: item.quantidade,
      modalidade_uuid: item?.modalidade_uuid ?? null,
      filial_uuid: atendimentosStore.getFilialSelecionada?.uuid ?? null
    }));

    const promocoes = promocoesItems.map(item => ({
      uuid: item.uuid,
      quantidade: item.quantidade,
      modalidade_uuid: item?.modalidade_uuid ?? null,
      filial_uuid: atendimentosStore.getFilialSelecionada?.uuid ?? null
    }));

    const data = {
      codigo_promocional: cupom,
      produtos: produtos,
      promocoes: promocoes
    }

    const response = await ApplyCupomApi(data)

    if (response.status === 'success') {
      response.data.produtos.forEach((p, index) => {
        const item = produtosItems[index]
        if (item) {
          item.valor_com_desconto = p.valor_com_desconto
        }
      })

      DescontoCarrinho.value = response.data.subtotal - response.data.total
      CupomProdutos.value = response.data.produtos.map(p => p.uuid)
      UpdateDescontoCupom()
      Cupom.value = cupom
      couponApplied.value = true
    } else {
      Notification.error(response.message)
      couponApplied.value = false
    }
  }

  function RemoveCupom() {
    Desconto.value = 0
    Cupom.value = ''
    // Limpa de todas as linhas do carrinho (não busca por uuid): o mesmo produto pode
    // ocupar mais de uma linha (modalidades diferentes) com o mesmo uuid.
    cartItemsCumputed.value.forEach(item => unset(item, 'valor_com_desconto'))

    CupomProdutos.value = []
    couponApplied.value = false
  }

  function UpdateDescontoCupom() {
    let desconto = cartItemsCumputed.value.map(item => {
      if (item.valor_com_desconto != null || item.valor_com_desconto != undefined) {
        return item.quantidade * (item.valor - item.valor_com_desconto)
      }
      return 0
    })

    Desconto.value = desconto.reduce((soma, item) => soma + item, 0)

    if (Desconto.value == 0) {
      RemoveCupom()
    }
  }

  async function GetInstallments(total = 0) {
    if (total == 0) {
      Installments.value = []
      return
    }

    try {
      const response = await request_anonymous.post('payment/card-installments/', { total: total })
      if (response.status == 200) {
        Installments.value = response.data.data.installments
        InterestRateInPercent.value = response.data.data?.interest_rate_in_percent ?? 0
      }
    } catch (error) {
      if (error.response?.data?.message) {
        Notification.error(`Erro ao buscar parcelas: ${error.response.data.message}`)
      } else {
        console.log(error)
      }
    }
  }

  watch(() => Subtotal.value, async () => {
    UpdateDescontoCupom()
  })


  return {
    showCart,
    cartItemsCumputed,
    cartItems,
    alertAddCartItems,
    TotalItensInCart,
    ShoppingCart,
    Cupom,
    CupomProdutos,
    couponApplied,
    Installments,
    InterestRateInPercent,
    SetAffiliateCode,
    AddProductToCart,
    RemoveProductFromCart,
    CleanPurchaseCart,
    IncrementProductInCart,
    DecrementProductInCart,
    ApplyCupom,
    RemoveCupom,
    GetInstallments,
    ChangeItemModality
  }

}, {
  persist: {
    pick: ['cartItems', 'Cupom', 'couponApplied']
  }
})
